import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function DistortionLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/distortion";
  const pageTitle = "Distortion in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what distortion is, how clipping changes sound, the difference between overdrive, distortion, and fuzz, and how distortion is used in music production and sound design.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "distortion",
          "audio distortion",
          "guitar distortion",
          "clipping",
          "overdrive",
          "fuzz",
          "saturation",
          "hard clipping",
          "soft clipping",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Distortion" },
          { "@type": "Thing", name: "Overdrive" },
          { "@type": "Thing", name: "Fuzz" },
          { "@type": "Thing", name: "Clipping" },
          { "@type": "Thing", name: "Saturation" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Distortion",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Distortion reshapes the waveform and adds new harmonics, making a sound richer, harder, dirtier, or more aggressive.",
      icon: <ElectricBoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It can add energy, sustain, thickness, character, and emotional intensity to instruments, vocals, and sound design.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Guitars, bass, synths, drums, vocals, cinematic sound design, and even subtle mix saturation.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const distortionTypes = [
    {
      name: "Overdrive",
      description:
        "Typically softer clipping that feels responsive and amp-like, often preserving more dynamics and touch sensitivity.",
    },
    {
      name: "Distortion",
      description:
        "Usually more aggressive and compressed than overdrive, with heavier saturation and a more obviously processed sound.",
    },
    {
      name: "Fuzz",
      description:
        "Extreme clipping that can push the waveform toward a square-wave shape, creating a buzzy, wooly, raw texture.",
    },
    {
      name: "Saturation",
      description:
        "A subtler form of distortion often used to add harmonics, warmth, and density without sounding obviously harsh.",
    },
    {
      name: "Bit Crushing",
      description:
        "A digital type of distortion that reduces resolution and creates lo-fi, crunchy, grainy textures.",
    },
    {
      name: "Amp Distortion",
      description:
        "The natural breakup that happens when amp stages, speakers, transformers, or power supplies are pushed hard.",
    },
  ];

  const clippingTypes = [
    {
      name: "Soft Clipping",
      description:
        "Rounds off peaks more gently, often sounding smoother and more musical. Common in overdrive and tube-style behavior.",
    },
    {
      name: "Hard Clipping",
      description:
        "Cuts peaks off more abruptly, producing a tighter, sharper, and more aggressive tone.",
    },
    {
      name: "Asymmetrical Clipping",
      description:
        "Clips the positive and negative halves differently, often creating a more complex harmonic profile.",
    },
    {
      name: "Symmetrical Clipping",
      description:
        "Clips both halves more evenly, often emphasizing odd harmonics and a more focused, aggressive tone.",
    },
  ];

  const toneControls = [
    {
      name: "Drive / Gain",
      description:
        "Controls how much distortion is created by increasing the level into the clipping stage.",
    },
    {
      name: "Tone / EQ",
      description:
        "Shapes the frequency balance before or after the clipping section, affecting brightness, body, and bite.",
    },
    {
      name: "Level / Output",
      description:
        "Sets the overall output volume after distortion is applied.",
    },
    {
      name: "Filter / Bias",
      description:
        "Advanced controls that can change the texture, harshness, symmetry, or feel of the distortion.",
    },
    {
      name: "Dry / Wet Mix",
      description:
        "Blends clean signal with distorted signal, useful for parallel-style processing and preserving clarity.",
    },
  ];

  const creativeUses = [
    "Guitar riffs, solos, and heavy rhythm tones",
    "Bass tracks that need grind, aggression, or definition",
    "Synths and keyboards that need edge, warmth, or attitude",
    "Vocals for lo-fi, industrial, experimental, or aggressive textures",
    "Parallel drum buses for punch and density",
    "Film and game audio for tension, malfunction, chaos, or damaged-machine sounds",
  ];

  const bestPractices = [
    "Use only as much distortion as the part needs. Too much can blur detail and articulation.",
    "Shape the frequency balance before and after distortion so the result sits better in a mix.",
    "Layer or blend distortion in parallel when you want grit without losing clarity.",
    "Different distortion styles suit different material: fuzz, overdrive, saturation, and hard clipping do not behave the same way.",
    "Always compare in context. A great solo tone in isolation can be too harsh or too dark inside a full mix.",
  ];

  const relatedLinks = [
    {
      title: "Overdrive",
      to: "/learning/overdrive",
      description:
        "See how overdrive differs from heavier distortion with softer clipping and a more touch-sensitive response.",
    },
    {
      title: "Compare Distortion, Overdrive, and Fuzz",
      to: "/learning/compare-distortion-overdrive-fuzz",
      description:
        "Compare the main gain families side by side and see how each one shapes tone and feel differently.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore the subtler side of distortion and how harmonic enhancement can add warmth and density.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compression interacts with distorted sounds and why gain structure affects sustain and punch.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Distortion | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Distortion
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Distortion is one of the most iconic and expressive effects in
                      music. It can turn a clean instrument into something thick,
                      aggressive, raw, or emotionally charged. Although many people
                      associate distortion mainly with electric guitar, it is equally
                      important in bass, synths, drums, vocals, mixing, and sound
                      design.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Is Distortion?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In audio, distortion broadly means the signal has been changed
                    from its original shape. In musical use, the term usually
                    refers to nonlinear distortion, where new harmonics are added
                    to the sound because the signal is being clipped, saturated,
                    overloaded, or otherwise pushed beyond a clean operating range.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    When peaks of the waveform are clipped, the sound becomes
                    richer in harmonics and often feels louder, denser, and more
                    intense. Depending on how it happens, distortion can be smooth
                    and warm, sharp and aggressive, or chaotic and broken.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Overdrive, Distortion, and Fuzz
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    These terms are related, but they do not mean exactly the same
                    thing. They sit on a spectrum of gain and clipping behavior.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {distortionTypes.map((type) => (
                      <Paper
                        key={type.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                          {type.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {type.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ElectricBoltRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How Distortion Works
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      At the center of distortion is clipping. Clipping happens
                      when a signal exceeds what a circuit or component can
                      reproduce cleanly. Instead of continuing smoothly, the
                      waveform is flattened or reshaped, which creates new
                      harmonics.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Distortion can appear in different places:
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Preamp stages</strong>{" "}
                        inside pedals or amplifiers
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Power amp stages</strong>{" "}
                        when an amp is pushed harder
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Speakers</strong>{" "}
                        through physical breakup and coloration
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>
                          Transformers and power supplies
                        </strong>{" "}
                        which can add saturation, sag, and feel
                      </li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Types of Clipping
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {clippingTypes.map((type) => (
                        <Paper
                          key={type.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {type.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {type.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Creative Uses of Distortion
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      {creativeUses.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </Box>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Distortion is not limited to heavy sounds. A little saturation
                      can make a source feel more alive, while more extreme settings
                      can completely reinvent the role of a sound.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Parameters and Tone Shaping
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {toneControls.map((control) => (
                        <Paper
                          key={control.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {control.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {control.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmberRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Avoiding Unwanted Distortion
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Distortion can be a powerful creative tool, but there are also
                    situations where it is undesirable. In clean production,
                    speech, acoustic recordings, and playback systems, engineers
                    often work to prevent unintentional clipping and overload.
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      pl: 2.5,
                      m: 0,
                      color: style.color3,
                      "& li": { mb: 1.15, lineHeight: 1.75 },
                    }}
                  >
                    <li>
                      <strong style={{ color: style.color4 }}>Limiters and compressors</strong>{" "}
                      help control peaks before unwanted clipping occurs.
                    </li>
                    <li>
                      <strong style={{ color: style.color4 }}>Headroom management</strong>{" "}
                      leaves enough level margin through the signal chain.
                    </li>
                    <li>
                      <strong style={{ color: style.color4 }}>Appropriate gain staging</strong>{" "}
                      keeps each device operating in its intended range.
                    </li>
                    <li>
                      <strong style={{ color: style.color4 }}>Clean amplification paths</strong>{" "}
                      reduce accidental breakup when clarity is the goal.
                    </li>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Final Thoughts
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Distortion began as a flaw or side effect of overloaded
                      equipment, but it became one of the most celebrated and
                      expressive sounds in music. It can add warmth, violence,
                      emotion, grit, and identity in ways that few other effects can.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      The most useful way to think about distortion is not as a
                      single sound, but as a palette. It can range from subtle
                      enhancement to total destruction, and every point on that
                      spectrum can be musically valuable.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related Learning Topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

// export default function DistortionLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/distortion",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <>
//         <div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//           <h1 className='h1-A' ref={topContainer}>Distortion</h1>
//           <Divider className="mobile-card-divider" />
//             <p className='p-A'>Distortion is one of the most iconic and versatile effects in music, sound design, and audio engineering. It can transform a clean guitar tone into a roaring wall of sound, bring a synth to life with saturation, or add intensity to cinematic moments through aggressive audio shaping. Though often associated with electric guitars, distortion plays a key role across a wide range of instruments, genres, and audio applications.</p>
//           <h2 className='h2-A'>What Is Distortion?</h2>
//             <p className='p-A'>In audio, distortion refers to any change in the shape or character of a sound wave — but more specifically, it usually means nonlinear distortion, where new frequencies are introduced to the original signal. These can be harmonically related (pleasant-sounding) or inharmonic (chaotic and noisy).</p>
//             <p className='p-A'>Distortion is most recognizable when an amplifier or signal path is overloaded beyond its intended capacity, causing the peaks of the waveform to be "clipped" — either gradually (soft clipping) or abruptly (hard clipping). This alters the harmonic content of the sound, resulting in everything from a warm, creamy breakup to a snarling, fuzzed-out growl.</p>
      
//             <h2 className='h2-A'>Overdrive, Distortion, and Fuzz: What's the Difference?</h2>
//           <p className='p-A'>Though these terms are often used interchangeably, they represent a spectrum of saturation:</p>
//           <ul className="a">
//             <li className="li-A"><strong><a href='/learning/overdrive'>Overdrive </a> </strong>typically refers to soft clipping, emulating the warm breakup of a cranked tube amp. It’s dynamic, responsive, and relatively subtle.</li>
//             <li className="li-A"><strong>Distortion</strong> is a more aggressive, consistent form of clipping — often hard clipping — with a denser, more compressed sound</li>
//             <li className="li-A"><strong>Fuzz</strong> takes things to the extreme, often shaping the waveform into something resembling a square wave. It’s buzzy, chaotic, and unapologetically lo-fi.</li>    
//             </ul>
//           <p className='p-A'>Each style has its own sonic fingerprint and is used to serve different creative purposes.</p>
//           <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'200px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="Boss DS-2 Turbo Distortion Pedal" bottomTextLinkUrl="https://www.boss.info/global/products/ds-2/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Boss_turbopedal_used.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/5/5d/Boss_turbopedal_used.jpg" alt = "Boss DS-2 Turbo Distortion Pedal with a scuffed and worn yellow finish"  title = "Dhscommtech, CC BY-SA 3.0 <https://creativecommons.org/licenses/by-sa/3.0>, via Wikimedia Commons" />
//       </div>
//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>A Brief History of Distortion</h2>
//           <h3 className='h3-A'>Early Accidents and Innovations</h3>
//           <p className='p-A'>Distortion as an intentional effect began with accidents. In the 1940s and ‘50s, guitarists like Junior Barnard and Willie Johnson would push their amplifiers beyond their limits, achieving gritty tones that paralleled the rawness of blues vocalists. Damaged amps, faulty preamps, and ripped speaker cones all contributed to early distorted sounds.</p>
//           <ul className="a">
//             <li className="li-A">In 1951, the song “Rocket 88” by Ike Turner’s band featured a distorted guitar tone due to a damaged speaker — an early example of fuzz on record.</li>
//             <li className="li-A">Chuck Berry’s "Maybellene" (1955) showcased warm tube-driven distortion that would inspire a generation.</li>
//             <li className="li-A">Link Wray punctured speaker cones with pencils to create the aggressive, lo-fi sound of his 1958 instrumental "Rumble."</li>    
//         </ul>
//             <h3 className='h3-A'>The Rise of Fuzzboxes and Pedals</h3>
//           <p className='p-A'>By the 1960s, the desire to control and replicate distortion led to the birth of fuzz pedals.</p>
//           <ul className="a">
//             <li className="li-A"><strong>Grady Martin’s</strong> session for Marty Robbins’ “Don’t Worry” (1961) introduced a broken preamp fuzz tone that inspired the Maestro FZ-1 Fuzz-Tone, one of the first commercially available fuzz pedals.</li>
//             <li className="li-A"><strong><a href="https://www.keithrichards.com/" target="_blank" rel="noopener noreferrer">Keith Richards</a></strong> used the Maestro FZ-1 for “(I Can’t Get No) Satisfaction” (1965), igniting a fuzz craze.</li>
//             <li className="li-A"><strong><a href="https://thekinks.info/" target="_blank" rel="noopener noreferrer">The Kinks</a>'</strong> Dave Davies slashed his speaker cone for “You Really Got Me” (1964), unleashing a wild, mid-heavy distortion that became a rock signature.</li>
               
//         </ul>
//         <iframe width="95%" height='275px' style={{maxWidth:'520px', marginLeft:'5%', marginRight:'5px', marginBottom:'25px'}} src="https://www.youtube.com/embed/fTTsY-oz6Go?si=_m6ZOPchAWbxA_xI" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
//           <p className='p-A'>By the late '60s, a golden era of fuzz pedals emerged:</p>
//           <ul className="a">
//             <li className="li-A"><strong><a href="https://www.ehx.com/products/big-muff-pi">Electro-Harmonix Big Muff Pi</a></strong> — used by <a href="https://www.jimihendrix.com/">Jimi Hendrix</a>, <a href="https://www.davidgilmour.com/">David Gilmour</a>, and <a href="https://www.santana.com/">Carlos Santana</a></li>
//             <li className="li-A"><strong>Arbiter <a href="https://www.jimdunlop.com/fuzz-face-distortion/">Fuzz Face</a></strong> - another <a href="https://www.jimihendrix.com/">Hendrix</a> favorite</li>
//             <li className="li-A"><strong><a href="https://voxamps.com/">Vox Tone Bender</a></strong> - famously used by <a href="https://www.thebeatles.com/">The Beatles</a></li>    
//         </ul>
//           <p className='p-A'>Simultaneously, amp builders like Jim Marshall began designing tube amps that deliberately broke up at high volumes, creating the rich, crunchy distortion that defined British rock and heavy metal.</p>
//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>How Distortion Works: The Science Behind the Sound</h2>
//           <p className='p-A'>At its core, distortion is the result of clipping, which occurs when a signal exceeds the limits of its circuit or component. There are several types of clipping:</p>
//           <ul className="a">
//             <li className="li-A"><strong>Soft Clipping:</strong> Gentle saturation that adds even-order harmonics, resulting in a warm, musical tone. This is typical of tube amps and overdrive pedals.</li>
//             <li className="li-A"><strong>Hard Clipping:</strong> Harsh, square-wave-like saturation with a strong presence of odd-order harmonics, often heard in solid-state distortion pedals and fuzz effects.</li>
//             <li className="li-A"><strong>Asymmetrical Clipping:</strong> Adds both even and odd harmonics, creating more complexity and richness.</li>    
//             <li className="li-A"><strong>Symmetrical Clipping:</strong> Focuses on odd harmonics, which can sound more "metallic" or aggressive.</li>    

//         </ul>
//           <p className='p-A'>Clipping can be introduced at various stages:</p>
//           <ul className="a">
//             <li><strong>Preamp:</strong> The first gain stage in an amp or pedal </li>
//             <li><strong>Power Amp:</strong> The final amplification stage, often with more compression and dynamic warmth </li>
//             <li><strong>Speakers:</strong> Physical speaker breakup can add color, especially in low-efficiency guitar speakers </li>
//             <li><strong>Transformers and Power Supplies:</strong> Transformer saturation and "sag" in tube amps contribute to the feel and response of distortion </li>

          
//           </ul>

//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>Types of Distortion Effects</h2>
//           <h3 className='h3-A'>Overdrive Pedals</h3>
//           <p className='p-A'>Popular for blues, classic rock, and dynamic rhythm playing.</p>
//           <ul className="a">
//             <li>Examples: <strong><a href="https://www.ibanez.com/usa/products/detail/ts808_99.html" target="_blank" rel="noopener noreferrer">Ibanez Tube Screamer</a>, <a href="https://www.boss.info/global/products/bd-2/" target="_blank" rel="noopener noreferrer">Boss BD-2 Blues Driver</a></strong></li>
//           </ul>
//           <h3 className='h3-A'>Distortion Pedals</h3>
//           <p className='p-A'>Designed for heavier music with more aggressive tones.</p>
//           <ul className="a">
//             <li>Examples: <strong><a href="https://actentertainment.com/rat-2-distortion-pedal/" target="_blank" rel="noopener noreferrer">Pro Co RAT</a>, <a href="https://www.boss.info/global/products/ds-1/" target="_blank" rel="noopener noreferrer">Boss DS-1</a>, <a href="https://www.jimdunlop.com/mxr-distortion/" target="_blank" rel="noopener noreferrer">MXR Distortion+</a></strong></li>
//           </ul>
//           <h3 className='h3-A'>Fuzz Pedals</h3>
//           <p className='p-A'>Iconic for their thick, fuzzy character.</p>
//           <ul className="a">
//             <li>Examples: <strong><a href="https://www.ehx.com/products/big-muff-pi" target="_blank" rel="noopener noreferrer">Electro-Harmonix Big Muff</a>,<a href="https://www.jimdunlop.com/fuzz-face-distortion/" target="_blank" rel="noopener noreferrer"> Fuzz Face</a>, <a href="https://www.zvex.com/guitar-pedals/fuzz-factory-guitar-effects-pedal" target="_blank" rel="noopener noreferrer">ZVEX Fuzz Factory</a></strong></li>
//           </ul>
//           <h3 className='h3-A'>Saturation</h3>
//           <p className='p-A'>A subtle form of distortion that enhances harmonics and “glues” elements together in a mix. Commonly used in mastering, drums, and vocals.</p>
//           <h3 className='h3-A'>Bit Crushing</h3>
//           <p className='p-A'>Reduces resolution (bit depth) to create retro, lo-fi textures — often used on synths, drums, and sound design.</p>
//           <h3 className='h3-A'>Amp Distortion</h3>
//           <p className='p-A'>Natural tube overdrive from real amplifiers remains the gold standard for many players. Popular amps include:</p>
//           <ul className="a">
//             <li><strong><a href="https://www.marshall.com/us/en/product/jcm800-2203-vintage-reissue-head" target="_blank" rel="noopener noreferrer">Marshall JCM800</a></strong></li>
//             <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'250px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="Marshall JCM800" bottomTextLinkUrl="https://www.marshall.com/us/en/product/jcm800-2203-vintage-reissue-head" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Marshall_JCM800_amplifier.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/4/4f/Marshall_JCM800_amplifier.jpg" alt = "Marshall JCM800 half stack"  title = "John2112, CC BY-SA 4.0 <https://creativecommons.org/licenses/by-sa/4.0>, via Wikimedia Commons" />
//       </div>
//             <li><strong><a href="https://www.fender.com/products/59-bassman-ltd" target="_blank" rel="noopener noreferrer">Fender Bassman</a></strong></li>
//             <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'250px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="Fender Bassman" bottomTextLinkUrl="https://www.fender.com/products/59-bassman-ltd" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Bassman15.JPG" imageSource="https://upload.wikimedia.org/wikipedia/commons/d/de/Bassman15.JPG" alt = "Fender Bassman amplifier"  title = "The Interior, CC BY-SA 3.0 <https://creativecommons.org/licenses/by-sa/3.0>, via Wikimedia Commons" />
//       </div>
            
//             <li><strong><a href="https://www.mesaboogie.com/en-US/p/Amp/90s-Dual-Rectifier" target="_blank" rel="noopener noreferrer">Mesa/Boogie Dual Rectifier</a></strong></li>
//             <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'300px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="Mesa Boogie Dual Rectifier" bottomTextLinkUrl="https://www.mesaboogie.com/en-US/p/Amp/90s-Dual-Rectifier" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Mesa_Boogie_Dual_Rectifier_Tremoverb_100w_Head.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mesa_Boogie_Dual_Rectifier_Tremoverb_100w_Head.jpg" alt = "Mesa Boogie Dual Rectifier guitar amp head"  title = "ArtBrom from Seattle, CC BY-SA 2.0 <https://creativecommons.org/licenses/by-sa/2.0>, via Wikimedia Commons" />
//       </div>
//             <li><strong><a href="https://orangeamps.com/rockerverb-100-mkiii-head/" target="_blank" rel="noopener noreferrer">Orange Rockerverb</a></strong></li>
//             <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'250px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="Orange Rockerverb" bottomTextLinkUrl="https://commons.wikimedia.org/wiki/File:JaguarLes_Paul_%2B_Orange_Rig.jpg" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Bassman15.JPG" imageSource="https://upload.wikimedia.org/wikipedia/commons/6/6f/JaguarLes_Paul_%2B_Orange_Rig.jpg" alt = "Orange Rockerverb Amplifier"  title = "Roadside Guitars, CC BY-SA 2.0 <https://creativecommons.org/licenses/by-sa/2.0>, via Wikimedia Commons" />
//       </div>
//           </ul>
//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>Creative Uses of Distortion</h2>
//           <h3 className='h3-A'>In Music:</h3>
//           <ul className="a">
//             <li><strong>Guitar:</strong> The most obvious and iconic application. Used for riffs, solos, rhythm, and leads across genres from blues to black metal</li>
//             <li><strong>Bass:</strong> Adds grind and aggression. Essential in punk, metal, and SoundCloud rap</li>
//             <li><strong>Synths & Keys:</strong> Distortion can warm up sterile digital synths or push a Rhodes piano into psychedelic territory</li>
//             <li><strong>Vocals:</strong> Occasionally used for character, edge, or otherworldly effects (industrial, experimental, or lo-fi genres)</li>
//             <li><strong>Drums:</strong> Distortion on parallel drum buses adds weight and attitude.</li>

//           </ul>
//           <h3 className='h3-A'>In Film and Sound Design:</h3>
//           <ul className="a">
//             <li><strong>Tension and Chaos:</strong> Use distortion to intensify emotional impact or signal sensory overload.</li>
//             <li><strong>Sci-Fi & Fantasy:</strong> Emulate alien signals, damaged radios, or digital decay.</li>
//             <li><strong>Characterization:</strong> Distorted voices can imply madness, malfunction, or supernatural elements.</li>
//           </ul>
//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>Parameters and Tone Shaping</h2>
//           <p className='p-A'>Most distortion effects and plugins offer parameters to shape the sound:</p>
//           <ul className="a">
//             <li><strong>Drive / Gain:</strong> Controls how much distortion is applied</li>
//             <li><strong>Tone / EQ:</strong> Adjusts frequency content (bass, mids, treble)</li>
//             <li><strong>Level / Output:</strong> Controls volume post-distortion</li>
//             <li><strong>Filter / Bias:</strong> Advanced options for tweaking the character of the clipping</li>
//             <li><strong>Dry/Wet Mix:</strong> Blend between clean and distorted signal (especially useful in parallel processing)</li>
//           </ul>
//           <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>Avoiding Unwanted Distortion</h2>
//           <p className='p-A'>While distortion is desirable in creative contexts, it’s often avoided in professional audio production, especially for vocals, acoustic instruments, or clean playback in PA systems. Engineers use tools like:</p>
//           <ul className="a">
//             <li><strong>Limiters and compressors:</strong> Controls how much distortion is applied</li>
//             <li><strong>Headroom management:</strong> Adjusts frequency content (bass, mids, treble)</li>
//             <li><strong>High-wattage amplifiers</strong> and <strong>low-gain preamps</strong> for clean tones</li>
//         </ul>
//         <Divider className="mobile-card-divider" />
//           <h2 className='h2-A'>Distortion as a Creative Tool, Not a Flaw</h2>
//           <p className='p-A'>What began as a flaw — the natural result of pushing equipment too hard — has become one of music's most celebrated effects. Distortion can energize, dramatize, and elevate your sound. Whether you’re stacking fuzz pedals for a shoegaze wall of sound, using subtle saturation to fatten a bass line, or mangling samples for a glitchy score, distortion is an endlessly expressive tool.</p>
//           <ul className="a">
//             <li><strong>Limiters and compressors:</strong> Controls how much distortion is applied</li>
//             <li><strong>Headroom management:</strong> Adjusts frequency content (bass, mids, treble)</li>
//             <li><strong>High-wattage amplifiers</strong> and <strong>low-gain preamps</strong> for clean tones</li>
//         </ul>      
//         <h3 className='h3-A'>Popular Genres That Rely on Distortion:</h3>
//         <ul className="a">
//             <li><strong>Blues & Rock:</strong> Warm overdrive and amp breakup</li>
//             <li><strong>Punk & Metal:</strong> Heavy distortion and power amp clipping</li>
//             <li><strong>Grunge & Alt Rock:</strong> Lo-fi fuzz and amp feedback</li>
//             <li><strong>Electronic Music:</strong> Bit crushing, saturation, filter distortion</li>
//             <li><strong>Hip-Hop & Trap:</strong> Distorted 808s and vocal chops</li>
//             <li><strong>Lo-Fi & Indie:</strong> Characterful, imperfect analog-style grit</li>
//         </ul>           
//         <Divider className="mobile-card-divider" />
//           <p>Distortion is far more than noise — it’s a palette of expressive colors, ranging from gentle warmth to all-out chaos. With roots tracing back to early blues and rock pioneers, distortion has evolved into a sophisticated toolset for musicians, sound designers, and audio engineers.</p>
          
//          <br />
//          <br />
//          <br />
//         <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
//       </div>


        
//         </>

//     )
// }